import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './QuickPricingModal.css';

const QuickPricingModal = ({ isOpen, onClose, propertyId, unitTypeId, unitId, unitData, onApplyPrice }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (isOpen && propertyId && unitTypeId) {
      fetchQuickRecommendation();
    }
  }, [isOpen, propertyId, unitTypeId]);

  const fetchQuickRecommendation = async () => {
    setLoading(true);
    setError(null);
    setAnalysis(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/pricing-engine/quick-recommendation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: propertyId,
          unit_type_id: unitTypeId,
          unit_id: unitId || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch pricing recommendation');
      }

      const data = await response.json();
      setAnalysis(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching pricing recommendation:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyToUnit = async (price) => {
    if (!unitId || !onApplyPrice) return;
    
    setApplying(true);
    try {
      await onApplyPrice(unitId, price);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const handleApplyToAllUnits = async (price) => {
    if (!onApplyPrice || !unitTypeId) return;
    
    setApplying(true);
    try {
      await onApplyPrice(null, price, unitTypeId); // null unitId means apply to all of this type
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const handleOpenFullPricingEngine = () => {
    const url = `/pricing-engine?propertyId=${propertyId}&unitTypeId=${unitTypeId}`;
    navigate(url);
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (!isOpen) return null;

  const recommendedPrice = analysis?.market_clearing_price || 0;
  const priceMin = analysis?.price_range?.min || 0;
  const priceMax = analysis?.price_range?.max || 0;
  const priceMedian = analysis?.price_range?.median || 0;
  const currentPrice = unitData?.monthly_rent ? parseFloat(unitData.monthly_rent) : null;
  const comparableCount = analysis?.comparable_properties || 0;

  return (
    <div className="quick-pricing-modal-overlay" onClick={onClose}>
      <div className="quick-pricing-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="quick-pricing-modal-header">
          <h2>💰 Pricing Recommendation</h2>
          <button className="quick-pricing-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="quick-pricing-modal-body">
          {loading && (
            <div className="quick-pricing-loading">
              <div className="spinner"></div>
              <p>Analyzing market data...</p>
            </div>
          )}

          {error && (
            <div className="quick-pricing-error">
              <p>⚠️ {error}</p>
              <button onClick={fetchQuickRecommendation} className="retry-button">
                Retry
              </button>
            </div>
          )}

          {!loading && !error && analysis && (
            <>
              <div className="quick-pricing-summary">
                <div className="recommended-price-card">
                  <div className="recommended-price-label">Recommended Price</div>
                  <div className="recommended-price-value">{formatCurrency(recommendedPrice)}</div>
                  {currentPrice && (
                    <div className={`price-comparison ${currentPrice > recommendedPrice ? 'above' : currentPrice < recommendedPrice ? 'below' : 'equal'}`}>
                      {currentPrice > recommendedPrice 
                        ? `Currently ${formatCurrency(currentPrice)} (${formatCurrency(currentPrice - recommendedPrice)} above)`
                        : currentPrice < recommendedPrice
                        ? `Currently ${formatCurrency(currentPrice)} (${formatCurrency(recommendedPrice - currentPrice)} below)`
                        : 'Price is optimal'}
                    </div>
                  )}
                </div>

                <div className="price-range-info">
                  <div className="range-item">
                    <span className="range-label">Market Range:</span>
                    <span className="range-value">{formatCurrency(priceMin)} - {formatCurrency(priceMax)}</span>
                  </div>
                  <div className="range-item">
                    <span className="range-label">Median:</span>
                    <span className="range-value">{formatCurrency(priceMedian)}</span>
                  </div>
                  <div className="range-item">
                    <span className="range-label">Comparables:</span>
                    <span className="range-value">{comparableCount} properties</span>
                  </div>
                </div>
              </div>

              {analysis.market_insights && (
                <div className="market-insights">
                  <h3>Market Insights</h3>
                  <div className="insights-grid">
                    <div className="insight-item">
                      <span className="insight-label">Demand:</span>
                      <span className="insight-value">{analysis.market_insights.demand_level || 'Unknown'}</span>
                    </div>
                    <div className="insight-item">
                      <span className="insight-label">Competition:</span>
                      <span className="insight-value">{analysis.market_insights.competition_level || 'Unknown'}</span>
                    </div>
                    <div className="insight-item">
                      <span className="insight-label">Trend:</span>
                      <span className="insight-value">{analysis.market_insights.price_trend || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="quick-pricing-actions">
                {unitId ? (
                  <button 
                    onClick={() => handleApplyToUnit(recommendedPrice)}
                    disabled={applying}
                    className="apply-price-button primary"
                  >
                    {applying ? 'Applying...' : `Apply ${formatCurrency(recommendedPrice)} to This Unit`}
                  </button>
                ) : (
                  <button 
                    onClick={() => handleApplyToAllUnits(recommendedPrice)}
                    disabled={applying}
                    className="apply-price-button primary"
                  >
                    {applying ? 'Applying...' : `Apply ${formatCurrency(recommendedPrice)} to All Units of This Type`}
                  </button>
                )}
                
                <button 
                  onClick={handleOpenFullPricingEngine}
                  className="open-full-engine-button"
                >
                  Open Full Pricing Engine →
                </button>
              </div>
            </>
          )}

          {!loading && !error && !analysis && (
            <div className="quick-pricing-empty">
              <p>No pricing data available. Click "Open Full Pricing Engine" to run a full analysis.</p>
              <button 
                onClick={handleOpenFullPricingEngine}
                className="open-full-engine-button"
              >
                Open Full Pricing Engine →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickPricingModal;

